import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '~/components/Spinner';

import {
  getStreamsRevenueByVintage,
  getStreamsRevenueGrowthByVintage,
} from '../../../api/streams-by-vintage';
import { retryOn504Options } from '../../../constants/api';
import { useAmplifyUser } from '../../../context/useAmplifyUser';
import { NoAccess } from '../../LabelPerf/components';
import type { SeriesConfig } from '../Components/MultiLineChart';
import { MultiLineChart } from '../Components/MultiLineChart';
import type { StackBarSeriesConfig } from '../Components/StackBarChart';
import { StackBarChart } from '../Components/StackBarChart';
import {
  VINTAGE_COLORS,
  VINTAGE_LABELS,
  YOY_REVENUE_COLORS,
  YOY_REVENUE_LABELS,
} from '../GlobalStreamsByVintage/types';

const PAGE_TITLE = 'Global Revenue, by Vintage';

// YoY Revenue Growth chart series configuration
const YOY_REVENUE_GROWTH_SERIES: SeriesConfig[] = [
  {
    key: 'newRelease',
    color: YOY_REVENUE_COLORS.newRelease,
    label: YOY_REVENUE_LABELS.newRelease,
  },
  {
    key: 'shallow',
    color: YOY_REVENUE_COLORS.shallow,
    label: YOY_REVENUE_LABELS.shallow,
  },
  {
    key: 'deepCatalog',
    color: YOY_REVENUE_COLORS.deepCatalog,
    label: YOY_REVENUE_LABELS.deepCatalog,
  },
  {
    key: 'grandTotal',
    color: YOY_REVENUE_COLORS.grandTotal,
    label: YOY_REVENUE_LABELS.grandTotal,
  },
];

// Stacked bar chart series configuration (order determines stacking: first = bottom)
const STACK_BAR_SERIES: StackBarSeriesConfig[] = [
  {
    key: 'deepCatalog',
    color: VINTAGE_COLORS.deepCatalog,
    label: VINTAGE_LABELS.deepCatalog,
  },
  {
    key: 'shallow',
    color: VINTAGE_COLORS.shallow,
    label: VINTAGE_LABELS.shallow,
  },
  {
    key: 'newRelease',
    color: VINTAGE_COLORS.newRelease,
    label: VINTAGE_LABELS.newRelease,
  },
];

export function GlobalRevenueByVintage() {
  const { t } = useTranslation('label-perf');
  const { user, isLoading: isUserLoading } = useAmplifyUser();

  // Check if user has admin or supervisor role
  const hasPermission = user?.role === 'admin' || user?.role === 'supervisor';

  // Fetch YoY revenue growth data from API
  const {
    data: yoyGrowthData,
    isLoading: isYoyGrowthLoading,
    isError: isYoyGrowthError,
  } = useQuery({
    queryKey: ['streams-revenue-growth-by-vintage'],
    queryFn: getStreamsRevenueGrowthByVintage,
    ...retryOn504Options,
  });

  // Fetch stacked bar revenue data from API
  const {
    data: revenueData,
    isLoading: isRevenueLoading,
    isError: isRevenueError,
  } = useQuery({
    queryKey: ['streams-revenue-by-vintage'],
    queryFn: getStreamsRevenueByVintage,
    ...retryOn504Options,
  });

  // Process stacked bar chart data with dynamic Y-axis
  const { stackBarData, stackBarYMax, stackBarYTicks } = useMemo(() => {
    if (!revenueData || revenueData.length === 0) {
      return {
        stackBarData: [],
        stackBarYMax: 250,
        stackBarYTicks: [0, 50, 100, 150, 200, 250],
      };
    }

    // Calculate max total for Y-axis
    let maxTotal = 0;

    revenueData.forEach((point) => {
      const total = point.newRelease + point.shallow + point.deepCatalog;

      if (total > maxTotal) {
        maxTotal = total;
      }
    });

    // Round up to nice number
    const roundedMax = Math.ceil(maxTotal / 50) * 50;
    const computedMaxY = Math.max(roundedMax, 250);
    const step = computedMaxY / 5;
    const ticks = Array.from({ length: 6 }, (_, i) => i * step);

    return {
      stackBarData: revenueData,
      stackBarYMax: computedMaxY,
      stackBarYTicks: ticks,
    };
  }, [revenueData]);

  // Process YoY growth chart data with dynamic Y-axis
  const { yoyChartData, yoyYMax, yoyYMin, yoyYTicks } = useMemo(() => {
    if (!yoyGrowthData || yoyGrowthData.length === 0) {
      return {
        yoyChartData: [],
        yoyYMax: 15,
        yoyYMin: 0,
        yoyYTicks: [0, 5, 10, 15],
      };
    }

    // Find min and max values
    let minVal = 0;
    let maxVal = 0;

    yoyGrowthData.forEach((point) => {
      const values = [
        point.newRelease,
        point.shallow,
        point.deepCatalog,
        point.grandTotal,
      ];

      values.forEach((v) => {
        if (v < minVal) {
          minVal = v;
        }

        if (v > maxVal) {
          maxVal = v;
        }
      });
    });

    // Round to nice numbers
    const computedMinY = Math.floor(minVal / 5) * 5;
    const computedMaxY = Math.ceil(maxVal / 5) * 5;
    const range = computedMaxY - computedMinY;
    const step = range / 4;
    const ticks = Array.from({ length: 5 }, (_, i) => computedMinY + i * step);

    return {
      yoyChartData: yoyGrowthData,
      yoyYMax: computedMaxY,
      yoyYMin: computedMinY,
      yoyYTicks: ticks,
    };
  }, [yoyGrowthData]);

  const isLoading = isUserLoading || isYoyGrowthLoading || isRevenueLoading;
  const isError = isYoyGrowthError || isRevenueError;

  // Loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  // Permission check - only admin and supervisor roles can access
  if (!hasPermission) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient bg-[length:100%,auto] bg-fixed bg-no-repeat bg-level-01/full">
        <NoAccess />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient bg-[length:100%,auto] bg-fixed bg-no-repeat bg-level-01/full">
        <div className="text-center">
          <h2 className="mb-sm text-headline-sm text-default">
            {t('error.failedToLoadData', 'Failed to load data')}
          </h2>
          <p className="text-subtle">
            {t(
              'error.refreshOrContactSupport',
              'Please try refreshing the page or contact support if the issue persists.',
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-w-[800px] grow overflow-auto overscroll-contain bg-gradient bg-[length:100%,auto] bg-fixed bg-no-repeat bg-level-01/full">
      <div className="p-md sm:p-lg md:p-xl">
        {/* Header */}
        <h1 className="mb-2xl text-headline-sm font-bold text-default">
          {PAGE_TITLE}
        </h1>

        {/* YoY Streaming Revenue Growth Chart - API data */}
        <div className="mb-2xl">
          <MultiLineChart
            data={yoyChartData}
            series={YOY_REVENUE_GROWTH_SERIES}
            yAxisLabel="YoY % Growth"
            height={380}
            yTickValues={yoyYTicks}
            yMax={yoyYMax}
            yMin={yoyYMin}
            lineInterpolation="linear"
            showDataPointMarkers={true}
            dataPointMarkerSize={4}
            formatYTick={(value) => `${value}%`}
            formatEndLabel={(value) => `${value.toFixed(0)}%`}
            formatTooltipValue={(value) => `${value.toFixed(1)}%`}
          />
        </div>

        {/* Stacked Bar Chart Section - API data */}
        <div className="mb-2xl">
          <StackBarChart
            data={stackBarData}
            series={STACK_BAR_SERIES}
            height={380}
            yTickValues={stackBarYTicks}
            yMax={stackBarYMax}
            formatValue={(value) => `${Math.round(value)}M`}
            formatTotal={(value) => `${Math.round(value)}M`}
            showBarLabels={true}
            minLabelThreshold={15}
          />
        </div>
      </div>
    </div>
  );
}

GlobalRevenueByVintage.displayName = 'GlobalRevenueByVintage';
